import type { Security } from '../types/Security';
import { SecurityTypesUtils, type SecurityTypes } from '../types/SecurityTypes';
import {
  InstrumentCompositionEnum,
  PositionAssetTypeEnum,
  ProductTypeEnum,
  SettleValueTypeEnum,
  type SyntheticProductTypeEnum,
} from '../types/types';
import { toBigWithDefault } from './number';

/** The separator-free, case-insensitive symbol alias used by symbol search controls. */
export const getSearchSymbol = (symbol: string): string => symbol.replaceAll('-', '').toLowerCase();

/**
 * Returns true if the Security supports being traded in counter currency amounts.
 * Security needs a PositionCurrency https://talostrading.atlassian.net/wiki/spaces/AVA/pages/1097760782/How+to+use+currencies#Performing-conversions-for-derivatives
 */
export const canCcyFutureOrPerp = (security?: Security | undefined): boolean => {
  return !isSecurityQuantoFuture(security);
};

/**
 * Identifies Quanto contract by checking if SettlementCurrency is neither the base nor quote currency on the instrument.
 * https://talostrading.atlassian.net/wiki/spaces/AVA/pages/2199552112/Future+and+Perpetual+Contract+Types
 */
export const isSecurityQuantoFuture = (security?: Security) => {
  return (
    !!security &&
    security.ProductType !== ProductTypeEnum.Spot &&
    ![security.BaseCurrency, security.QuoteCurrency].includes(security.SettlementCurrency)
  );
};

export const isSecurityExpired = (security?: Security): boolean => {
  if (!security || !security.Expiration) {
    return false;
  }
  return new Date(security.Expiration) < new Date();
};

export const isSecurityCME = (security?: Security): boolean => {
  return security?.Markets?.includes('cme') || false;
};

export const isSecurityDeribit = (security?: Security): boolean => {
  return security?.Markets?.includes('deribit') || false;
};

export const isDeribitLinearOption = (security?: Security): security is SecurityTypes['Option'] => {
  if (!security) {
    return false;
  }

  return (
    isSecurityDeribit(security) &&
    SecurityTypesUtils.Option.isType(security) &&
    security.BaseCurrency !== security.QuoteCurrency &&
    security.SettleValueType === SettleValueTypeEnum.Regular
  );
};

/**
 * Returns the frontend option coin used to group related option contracts for selectors, expiries,
 * strikes, routing, and option-chain filtering.
 *
 * Some venues include display or venue-specific details in their raw option identifiers. The option
 * coin is always derived from base/quote instead.
 */
export function getOptionCoin(option: SecurityTypes['Option']): string {
  return option.BaseCurrency === option.QuoteCurrency
    ? option.BaseCurrency
    : `${option.BaseCurrency}-${option.QuoteCurrency}`;
}

export const isLinearOption = (security?: Security) => {
  if (!security) {
    return false;
  }

  return (
    security.SettlementCurrency === security.QuoteCurrency && security.SettlementCurrency !== security.BaseCurrency
  );
};

export const isInvertedOption = (security?: Security) => {
  if (!security) {
    return false;
  }

  return (
    security.SettlementCurrency === security.QuoteCurrency && security.SettlementCurrency === security.BaseCurrency
  );
};

export const isSpot = (security?: Security) => {
  return SecurityTypesUtils.Spot.isProductType(security);
};

export const isCompound = (security?: Security) => {
  return SecurityTypesUtils.Compound.isProductType(security);
};

export const isPerpetualSwap = (security?: Security) => {
  return SecurityTypesUtils.Perp.isProductType(security);
};

export const isInversePerp = (security?: Security) => {
  return isPerpetualSwap(security) && security?.SettleValueType === SettleValueTypeEnum.Inverted;
};

export const isLinearPerp = (security?: Security) => {
  return isPerpetualSwap(security) && security?.SettleValueType === SettleValueTypeEnum.Regular;
};

export const isFuture = (security?: Security) => {
  return SecurityTypesUtils.Future.isProductType(security);
};

export const isCFD = (security?: Security) => {
  return SecurityTypesUtils.CFD.isProductType(security);
};

export const isETF = (security?: Security) => {
  return SecurityTypesUtils.ETF.isProductType(security);
};

export const isOption = (security?: Security) => {
  return SecurityTypesUtils.Option.isProductType(security);
};

export const isDerivative = (security?: Security) => {
  return isFuture(security) || isPerpetualSwap(security) || isOption(security) || isCFD(security);
};

export const isMultileg = (security?: Security) => {
  return SecurityTypesUtils.Multileg.isType(security);
};

export const isDelta1Multileg = (security?: Security) => {
  return SecurityTypesUtils.Delta1Multileg.isType(security);
};

export const supportsBPSPricing = (security?: Security): boolean => {
  return Boolean(security?.SupportedPricingModes?.BPS);
};

export const isCross = (security?: Security) => {
  return SecurityTypesUtils.CrossMultileg.isType(security);
};

export const isCalendarSpread = (security?: Security) => {
  return SecurityTypesUtils.CalendarSpread.isType(security);
};

export const isOptionStrategy = (security?: Security) => {
  return SecurityTypesUtils.OptionStrategy.isType(security);
};

export const isBasis = (security?: Security) => {
  return SecurityTypesUtils.Basis.isType(security);
};

export const getProductType = (security?: Security) => {
  if (!security) {
    return '';
  }

  if (isMultileg(security) && !security?.MultilegDetails?.SyntheticProductType) {
    return '';
  }

  return isMultileg(security)
    ? productTypeToString(security.MultilegDetails!.SyntheticProductType)
    : productTypeToString(security.ProductType);
};

// Not the same as ProductTypeEnum since it's not 1:1, i.e. Multi isn't a ProductType
export type ProductGroup =
  | 'Spot'
  | 'Perp'
  | 'Future'
  | 'Multi'
  | 'Option'
  | 'Basis'
  | 'CFD'
  | 'Share'
  | 'Synthetic'
  | 'Unknown'
  | 'Cross'
  | 'Delta1Spread'
  | 'CallCalendarSpread'
  | 'PutCalendarSpread'
  | 'CalendarSpread'
  | 'CallSpread'
  | 'PutSpread'
  | 'OTC'
  | 'ExchangeTradedFund'
  | 'GovernmentBond'
  | 'Compound'
  | 'Prediction';

export const getProductGroupLabel = (productGroup: ProductGroup): string => {
  switch (productGroup) {
    case 'CalendarSpread':
      return 'Calendar Spread';
    case 'Delta1Spread':
      return 'Delta 1 Spread';
    case 'CallCalendarSpread':
      return 'Call Calendar Spread';
    case 'PutCalendarSpread':
      return 'Put Calendar Spread';
    case 'CallSpread':
      return 'Call Spread';
    case 'PutSpread':
      return 'Put Spread';
    case 'ExchangeTradedFund':
      return 'ETF';
    case 'GovernmentBond':
      return 'Government Bond';
    case 'CFD':
      return 'Swap';
    case 'Compound':
      return 'Fund';
    case 'OTC':
      return 'OTC / Loan';
    default:
      return productGroup;
  }
};

export const productTypeToString = (productType: ProductTypeEnum | SyntheticProductTypeEnum): ProductGroup => {
  switch (productType) {
    case ProductTypeEnum.PerpetualSwap:
      return 'Perp';
    case ProductTypeEnum.PredictionOutcome:
      return 'Prediction';
    default:
      return productType;
  }
};

export const productTypeLabel = (productType: ProductTypeEnum | SyntheticProductTypeEnum): string => {
  return getProductGroupLabel(productTypeToString(productType));
};

/** PositionAssetTypeEnum is a newer enum that clarifies OTC products.  We are currently discussing
 * the deprecation of it in the near future after we migrate Product Type to an updated model for this.
 */
export function positionAssetTypeLabel(assetType: PositionAssetTypeEnum | keyof typeof PositionAssetTypeEnum): string {
  const resolved =
    typeof assetType === 'string' && assetType in PositionAssetTypeEnum
      ? PositionAssetTypeEnum[assetType as keyof typeof PositionAssetTypeEnum]
      : (assetType as PositionAssetTypeEnum);

  switch (resolved) {
    case PositionAssetTypeEnum.OTCForward:
      return 'OTC Forward';
    case PositionAssetTypeEnum.OTCVanillaOption:
      return 'OTC Option';
    case PositionAssetTypeEnum.LoanFixedRate:
      return 'Fixed Rate Loan';
    case PositionAssetTypeEnum.LoanFloatingRate:
      return 'Floating Rate Loan';
    case PositionAssetTypeEnum.OTCRepo:
      return 'Repo';
    default:
      return productTypeLabel(resolved satisfies ProductTypeEnum);
  }
}

export const getGroup = (sec: Security): ProductGroup =>
  sec.Composition === InstrumentCompositionEnum.Synthetic ? 'Multi' : productTypeToString(sec.ProductType);

export const SpreadUnit = {
  BPS: 'BPS',
  PRICE: 'Price',
} as const;
export type SpreadUnit = (typeof SpreadUnit)[keyof typeof SpreadUnit];

export function getSpreadUnit(security?: Security, isInBPSMode?: boolean): SpreadUnit {
  // Check BPS mode first because delta1spread falls under isBasis otherwise
  if (isInBPSMode && supportsBPSPricing(security)) {
    return SpreadUnit.BPS;
  }
  if (isOption(security) || isBasis(security)) {
    return SpreadUnit.PRICE;
  }
  return SpreadUnit.BPS;
}

// Contracts are allowed if NotionalMultiplier != 1
// Mandatory and enforced for Quanto Symbols
export const allowContracts = (security?: Security) =>
  security &&
  (!toBigWithDefault(security?.NotionalMultiplier, 1).eq(1) ||
    isSecurityQuantoFuture(security) ||
    isDeribitLinearOption(security) ||
    SecurityTypesUtils.PredictionOutcome.isType(security));

export const getMinIncrementForContract = (security: Security, currency: string | undefined): string | undefined => {
  if (
    (security.SettlementCurrency === security.BaseCurrency && currency === security.QuoteCurrency) ||
    (security.SettlementCurrency === security.QuoteCurrency && currency === security.BaseCurrency)
  ) {
    return security.NotionalMultiplier;
  }
};

export const canViewDeepDive = (security?: Security) => {
  return security != null;
};

export const isExpired = (security?: Security) => {
  return security?.Expiration != null && new Date(security.Expiration) < new Date();
};
